import { useCallback, useEffect, useRef } from 'react'

interface RecaptchaConfig {
  siteKey?: string
  action?: string
}

interface RecaptchaInstance {
  ready: (callback: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha: RecaptchaInstance
  }
}

export const useRecaptcha = (config: RecaptchaConfig) => {
  const { siteKey, action = 'submit' } = config
  const scriptLoaded = useRef(false)
  const isReady = useRef(false)

  const loadRecaptchaScript = useCallback(() => {
    if (scriptLoaded.current || document.querySelector('script[src*="recaptcha"]')) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve, reject) => {
      if (!siteKey) {
        reject(new Error('reCAPTCHA site key is required'))
        return
      }
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
      script.async = true
      script.defer = true

      script.onload = () => {
        scriptLoaded.current = true
        resolve()
      }

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA script'))
      }

      document.head.appendChild(script)
    })
  }, [siteKey])

  const waitForRecaptcha = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (window.grecaptcha && isReady.current) {
        resolve()
        return
      }

      const checkRecaptcha = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            isReady.current = true
            resolve()
          })
        }
        else {
          setTimeout(checkRecaptcha, 100)
        }
      }

      checkRecaptcha()
    })
  }, [])

  const executeRecaptcha = useCallback(async (customAction?: string): Promise<string> => {
    try {
      if (!siteKey) {
        throw new Error('reCAPTCHA site key is required')
      }
      await loadRecaptchaScript()
      await waitForRecaptcha()

      const actionToUse = customAction || action
      const token = await window.grecaptcha.execute(siteKey, { action: actionToUse })

      return token
    }
    catch (error) {
      console.error('reCAPTCHA execution failed:', error)
      throw error
    }
  }, [siteKey, action, loadRecaptchaScript, waitForRecaptcha])

  useEffect(() => {
    loadRecaptchaScript().catch((error) => {
      console.error('Failed to load reCAPTCHA:', error)
    })
  }, [loadRecaptchaScript])

  return {
    executeRecaptcha,
    loadRecaptchaScript,
  }
}
